import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search,
  Eye,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Shield,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';
import CreateAccessFormModal from '../components/CreateAccessFormModal';

// Access Form Status Colors
function getAccessFormStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    PENDING_HR: 'bg-yellow-100 text-yellow-800',
    PENDING_IT: 'bg-blue-100 text-blue-800',
    APPROVED: 'bg-green-100 text-green-800',
    IMPLEMENTED: 'bg-emerald-100 text-emerald-800',
    REJECTED: 'bg-red-100 text-red-800',
    REVOKED: 'bg-orange-100 text-orange-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

// Access Type Colors
function getAccessTypeColor(type: string): string {
  const colors: Record<string, string> = {
    NEW_HIRE: 'bg-blue-100 text-blue-800',
    ROLE_CHANGE: 'bg-purple-100 text-purple-800',
    TEMPORARY: 'bg-yellow-100 text-yellow-800',
    CONTRACTOR: 'bg-orange-100 text-orange-800',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}

// Status Badge Component
function StatusBadge({ status }: { status: string }) {
  const getIcon = () => {
    switch (status) {
      case 'APPROVED':
      case 'IMPLEMENTED':
        return <CheckCircle2 className="w-3 h-3" />;
      case 'REJECTED':
      case 'REVOKED':
        return <XCircle className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  return (
    <span className={cn(
      "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full",
      getAccessFormStatusColor(status)
    )}>
      {getIcon()}
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export default function AccessFormsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [accessType, setAccessType] = useState(searchParams.get('accessType') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [showFilters, setShowFilters] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Handle action=new query parameter to open create modal
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setIsCreateModalOpen(true);
      // Remove action param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (accessType) params.set('accessType', accessType);
    setSearchParams(params);
  }, [page, search, status, accessType, setSearchParams]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['accessForms', { page, search, status, accessType }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (accessType) params.append('accessType', accessType);
      
      const response = await api.get(`/access-forms?${params.toString()}`);
      return response.data;
    },
  });

  const forms = data?.data || [];
  const pagination = data?.pagination;

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setAccessType('');
    setPage(1);
  };

  const hasActiveFilters = search || status || accessType;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8" />
            Access Forms
          </h1>
          <p className="text-muted-foreground">
            Manage employee access requests and approvals
            {pagination && ` • ${pagination.total} total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Access Form
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by form number, employee name..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="PENDING_HR">Pending HR</option>
                <option value="PENDING_IT">Pending IT</option>
                <option value="APPROVED">Approved</option>
                <option value="IMPLEMENTED">Implemented</option>
                <option value="REJECTED">Rejected</option>
                <option value="REVOKED">Revoked</option>
              </select>
              <Button 
                variant={showFilters ? "secondary" : "outline"} 
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
              </Button>
            </div>

            {/* Extended Filters Row */}
            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                  value={accessType}
                  onChange={(e) => {
                    setAccessType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Access Types</option>
                  <option value="NEW_HIRE">New Hire</option>
                  <option value="ROLE_CHANGE">Role Change</option>
                  <option value="TEMPORARY">Temporary</option>
                  <option value="CONTRACTOR">Contractor</option>
                </select>
                {hasActiveFilters && (
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Forms Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No access forms found</p>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters 
                  ? "Try adjusting your filters"
                  : "Create your first access form to get started"}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Access Form
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Form #</th>
                    <th className="text-left p-4 font-medium">Employee</th>
                    <th className="text-left p-4 font-medium">Access Type</th>
                    <th className="text-left p-4 font-medium">Job Role</th>
                    <th className="text-left p-4 font-medium">Access Level</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Start Date</th>
                    <th className="text-left p-4 font-medium">Created</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {forms.map((form: any) => (
                    <tr key={form.id} className="hover:bg-muted/50 transition-colors">
                      <td className="p-4">
                        <Link 
                          to={`/access-forms/${form.id}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {form.formNumber}
                        </Link>
                      </td>
                      <td className="p-4">
                        <div>
                          {form.employee ? (
                            <>
                              <p className="font-medium text-sm">
                                {form.employee.firstName} {form.employee.lastName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {form.employee.email}
                              </p>
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getAccessTypeColor(form.accessType)
                        )}>
                          {form.accessType.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        {form.jobRole || '-'}
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          form.accessLevel === 'ADMIN' ? 'bg-red-100 text-red-800' :
                          form.accessLevel === 'WRITE' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        )}>
                          {form.accessLevel}
                        </span>
                      </td>
                      <td className="p-4">
                        <StatusBadge status={form.status} />
                      </td>
                      <td className="p-4 text-sm">
                        {form.startDate ? formatDate(form.startDate) : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {formatDate(form.createdAt)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild title="View Details">
                            <Link to={`/access-forms/${form.id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} forms
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Access Form Modal */}
      <CreateAccessFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          refetch();
        }}
      />
    </div>
  );
}
